import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";

const DEFAULT_PAGE_AWARENESS = {
  routeKey: "",
  routePath: "",
  pageTitle: "",
  pageType: "page",
  advertiserId: null,
  advertiserName: "",
  campaignId: null,
  campaignName: "",
  timeframe: null,
  platformKey: "",
  platformLabel: "",
  toplineScope: "aggregate",
  sectionKey: "",
  sectionLabel: "",
  pageCapabilities: [],
  pageToplineMetrics: [],
  aggregateToplineMetrics: [],
  metadata: {},
};

const PageAwarenessContext = createContext({
  pageAwareness: DEFAULT_PAGE_AWARENESS,
  registerPageAwareness: () => {},
  clearPageAwareness: () => {},
});

function isPlainObject(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function safeStringify(value) {
  try {
    return JSON.stringify(value ?? null);
  } catch (_err) {
    return "__unserializable__";
  }
}

function sanitizeMetric(metric, index) {
  if (!metric || typeof metric !== "object") return null;
  const label = String(metric.label || "").trim();
  const value = metric.value == null ? "" : String(metric.value).trim();
  const meta = metric.meta == null ? "" : String(metric.meta).trim();
  const tone = metric.tone == null ? "" : String(metric.tone).trim();
  if (!label || !value) return null;
  return {
    id: String(metric.id || label || index),
    label,
    value,
    meta,
    tone,
  };
}

function normalizePageAwareness(input = {}, pathname = "") {
  const source = isPlainObject(input) ? input : {};

  const pageMetrics = Array.isArray(source.pageToplineMetrics || source.toplineMetrics)
    ? (source.pageToplineMetrics || source.toplineMetrics)
        .map(sanitizeMetric)
        .filter(Boolean)
        .slice(0, 6)
    : [];
  const aggregateMetrics = Array.isArray(source.aggregateToplineMetrics)
    ? source.aggregateToplineMetrics.map(sanitizeMetric).filter(Boolean).slice(0, 6)
    : [];

  return {
    ...DEFAULT_PAGE_AWARENESS,
    ...source,
    routeKey: String(source.routeKey || pathname || "").trim(),
    routePath: String(pathname || source.routePath || "").trim(),
    pageTitle: String(source.pageTitle || "").trim(),
    pageType: String(source.pageType || "page").trim(),
    advertiserId: source.advertiserId ?? null,
    advertiserName: String(source.advertiserName || "").trim(),
    campaignId: source.campaignId ?? null,
    campaignName: String(source.campaignName || "").trim(),
    platformKey: String(source.platformKey || "").trim(),
    platformLabel: String(source.platformLabel || "").trim(),
    toplineScope:
      source.toplineScope === "page" || source.toplineScope === "aggregate"
        ? source.toplineScope
        : pageMetrics.length
        ? "page"
        : "aggregate",
    sectionKey: String(source.sectionKey || "").trim(),
    sectionLabel: String(source.sectionLabel || "").trim(),
    pageCapabilities: Array.isArray(source.pageCapabilities)
      ? source.pageCapabilities
          .map((item) => String(item || "").trim())
          .filter(Boolean)
          .slice(0, 8)
      : [],
    pageToplineMetrics: pageMetrics,
    aggregateToplineMetrics: aggregateMetrics,
    metadata: isPlainObject(source.metadata) ? source.metadata : {},
  };
}

function sameMetricLists(a = [], b = []) {
  if (a === b) return true;
  if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
  return a.every((item, index) => {
    const other = b[index];
    return (
      item?.id === other?.id &&
      item?.label === other?.label &&
      item?.value === other?.value &&
      item?.meta === other?.meta &&
      item?.tone === other?.tone
    );
  });
}

function samePageAwareness(a = DEFAULT_PAGE_AWARENESS, b = DEFAULT_PAGE_AWARENESS) {
  if (!a || !b) return false;
  return (
    a.routeKey === b.routeKey &&
    a.routePath === b.routePath &&
    a.pageTitle === b.pageTitle &&
    a.pageType === b.pageType &&
    a.advertiserId === b.advertiserId &&
    a.advertiserName === b.advertiserName &&
    a.campaignId === b.campaignId &&
    a.campaignName === b.campaignName &&
    a.platformKey === b.platformKey &&
    a.platformLabel === b.platformLabel &&
    a.toplineScope === b.toplineScope &&
    a.sectionKey === b.sectionKey &&
    a.sectionLabel === b.sectionLabel &&
    safeStringify(a.timeframe || null) === safeStringify(b.timeframe || null) &&
    safeStringify(a.pageCapabilities || []) === safeStringify(b.pageCapabilities || []) &&
    sameMetricLists(a.pageToplineMetrics, b.pageToplineMetrics) &&
    sameMetricLists(a.aggregateToplineMetrics, b.aggregateToplineMetrics) &&
    safeStringify(a.metadata || {}) === safeStringify(b.metadata || {})
  );
}

export function PageAwarenessProvider({ children }) {
  const location = useLocation();
  const pathname = String(location?.pathname || "").trim();
  const [pageAwareness, setPageAwareness] = useState(() =>
    normalizePageAwareness({}, pathname)
  );

  useEffect(() => {
    setPageAwareness((prev) => {
      const next = normalizePageAwareness({}, pathname);
      return samePageAwareness(prev, next) ? prev : next;
    });
  }, [pathname]);

  const registerPageAwareness = useCallback(
    (nextValue) => {
      setPageAwareness((prev) => {
        const next = normalizePageAwareness(nextValue, pathname);
        return samePageAwareness(prev, next) ? prev : next;
      });
    },
    [pathname]
  );

  const clearPageAwareness = useCallback(() => {
    setPageAwareness((prev) => {
      const next = normalizePageAwareness({}, pathname);
      return samePageAwareness(prev, next) ? prev : next;
    });
  }, [pathname]);

  const value = useMemo(
    () => ({
      pageAwareness,
      registerPageAwareness,
      clearPageAwareness,
    }),
    [pageAwareness, registerPageAwareness, clearPageAwareness]
  );

  return (
    <PageAwarenessContext.Provider value={value}>
      {children}
    </PageAwarenessContext.Provider>
  );
}

export function usePageAwarenessState() {
  return useContext(PageAwarenessContext);
}
